import {
	BOLD_ITALIC_STAR,
	BOLD_ITALIC_UNDERSCORE,
	BOLD_STAR,
	BOLD_UNDERSCORE,
	HEADING,
	ITALIC_STAR,
	ITALIC_UNDERSCORE,
	LINK,
	ORDERED_LIST,
	QUOTE,
	STRIKETHROUGH,
	TRANSFORMERS,
	UNORDERED_LIST,
} from "@lexical/markdown";
import { AutoLinkPlugin } from "@lexical/react/LexicalAutoLinkPlugin";
import { CharacterLimitPlugin } from "@lexical/react/LexicalCharacterLimitPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { PlainTextPlugin } from "@lexical/react/LexicalPlainTextPlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { EditorState } from "lexical";
import React from "react";

import { EditorFeature } from "..";
import AutofocusPlugin from "../plugins/AutoFocusPlugin";
import { HashtagSuggestionPlugin } from "../plugins/HashtagSuggestionPlugin";
import css from "../theme/theme.module.scss";

// Regex for matching URLs
const URL_MATCHER =
	/((https?:\/\/(www\.)?)|(www\.))[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/;

// Used with AutoLinkPlugin to determine matches
const AUTO_LINK_MATCHERS = [
	(text) => {
		const match = URL_MATCHER.exec(text);
		if (match === null) {
			return null;
		}
		const fullMatch = match[0];
		return {
			index: match.index,
			length: fullMatch.length,
			text: fullMatch,
			url: fullMatch.startsWith("http") ? fullMatch : `https://${fullMatch}`,
			attributes: { rel: "noopener", target: "_blank" },
		};
	},
];

const getMarkdownTransformers = (disabledFeatures?: EditorFeature[]) => {
	// If no features are disabled, return the default transformers
	if (!disabledFeatures?.length) return TRANSFORMERS;

	// Otherwise, start with the default transformers
	const transformers = [...TRANSFORMERS];

	// Remove the transformers for any disabled features
	disabledFeatures.forEach((feature) => {
		switch (feature) {
			case "bold":
				transformers.splice(transformers.indexOf(BOLD_ITALIC_STAR), 1);
				transformers.splice(transformers.indexOf(BOLD_ITALIC_UNDERSCORE), 1);
				transformers.splice(transformers.indexOf(BOLD_STAR), 1);
				transformers.splice(transformers.indexOf(BOLD_UNDERSCORE), 1);
				break;
			case "italic":
				transformers.splice(transformers.indexOf(BOLD_ITALIC_STAR), 1);
				transformers.splice(transformers.indexOf(BOLD_ITALIC_UNDERSCORE), 1);
				transformers.splice(transformers.indexOf(ITALIC_STAR), 1);
				transformers.splice(transformers.indexOf(ITALIC_UNDERSCORE), 1);
				break;
			case "strikethrough":
				transformers.splice(transformers.indexOf(STRIKETHROUGH), 1);
				break;
			case "link":
				transformers.splice(transformers.indexOf(LINK), 1);
				break;
			case "quote":
				transformers.splice(transformers.indexOf(QUOTE), 1);
				break;
			case "heading":
				transformers.splice(transformers.indexOf(HEADING), 1);
				break;
			case "list":
				transformers.splice(transformers.indexOf(UNORDERED_LIST), 1);
				transformers.splice(transformers.indexOf(ORDERED_LIST), 1);
				break;
		}
	});

	return transformers;
};

export const getEditorPlugins = (
	isRichText: boolean,
	onChange: (editorState: EditorState) => void,
	disabledFeatures?: EditorFeature[],
	placeholder?: string,
	hashTags?: string[],
	readOnly?: boolean,
	maxLength?: number,
	autoFocus = false
) => {
	const linksDisabled = disabledFeatures?.includes("link");
	const isPlainText = !isRichText;
	const listDisabled = disabledFeatures?.includes("list");

	return (
		<div className={css.editorInner}>
			{isRichText && (
				<>
					{/* base rich text capabilities: https://lexical.dev/docs/packages/lexical-rich-text */}
					<RichTextPlugin
						contentEditable={
							<ContentEditable
								spellCheck={false}
								className={readOnly ? css.editorInputReadOnly : css.editorInput}
							/>
						}
						placeholder={<div className={css.editorPlaceholder}>{placeholder}</div>}
						ErrorBoundary={LexicalErrorBoundary}
					/>
					{/* enables import/export of markdown. provides md shortcuts.: https://lexical.dev/docs/packages/lexical-markdown */}
					<MarkdownShortcutPlugin transformers={getMarkdownTransformers()} />
				</>
			)}

			{isRichText && !linksDisabled && (
				<>
					<LinkPlugin />
					<AutoLinkPlugin matchers={AUTO_LINK_MATCHERS} />
				</>
			)}

			{isRichText && !listDisabled && <ListPlugin />}

			{isPlainText && (
				<>
					<PlainTextPlugin
						contentEditable={
							<ContentEditable
								spellCheck={false}
								className={readOnly ? css.editorInputReadOnly : css.editorInput}
							/>
						}
						placeholder={<div className={css.editorPlaceholder}>{placeholder}</div>}
						ErrorBoundary={LexicalErrorBoundary}
					/>
				</>
			)}

			{maxLength && (
				<div className={css.characterLimitPlugin}>
					<CharacterLimitPlugin maxLength={maxLength} charset="UTF-8" />
				</div>
			)}

			{hashTags?.length && <HashtagSuggestionPlugin hashTags={hashTags} />}

			{autoFocus && <AutofocusPlugin />}

			<OnChangePlugin onChange={onChange} />
		</div>
	);
};
